import { RootState } from "src/store";
import { connect } from "react-redux";
import { filterActions } from "../../filter-store";
import { memo, useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Model_ConfigurationAttributeGet } from "src/backend/openapi/v1";
import { TAttributeOptionsType, TAttributeValueFilter } from "../../filter-types";
import { getAttributeDisplayName } from "src/pages/configurator/configurator-utils";

const mapStateToProps = (state: RootState) => ({
  attributesCatalog: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.attributesCatalog,
  formData: state.pageReducer.combinedConfiguratorReducer.filterReducer.formData
});

const ownActions = {
  setFormDataAttributes: filterActions.setFormDataAttributes
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

type RowProps = {
  attribute: Model_ConfigurationAttributeGet;
  selectedValues: TAttributeValueFilter[] | undefined;
  onChange: typeof filterActions.setFormDataAttributes;
};

// One field per catalog attribute, and the catalog carries an entry per product type — so this list
// can run to dozens of Autocompletes. Memoized per attribute (and with its options/value arrays built
// once) so selecting a value in one field doesn't re-render and re-filter every other field.
const AttributeFilterRow = memo(({ attribute, selectedValues, onChange }: RowProps) => {
  const displayName = getAttributeDisplayName(attribute);

  const options = useMemo(() => attribute.values.filter(x => x.id && x.description).map((x): TAttributeOptionsType => ({ description: x.description, id: x.id })), [attribute.values]);

  const value = useMemo(() => (selectedValues ?? []).filter(x => x.description && x.id).map((x): TAttributeOptionsType => ({ description: x.description || "", id: x.id || "" })), [selectedValues]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, values) => onChange({ id: attribute.id, name: displayName, values })}
      size="small"
      multiple
      options={options}
      getOptionLabel={(option: TAttributeOptionsType) => option.description || ""}
      // Selected options are already removed from the list by filterSelectedOptions, so no
      // getOptionDisabled is needed on top of it
      filterSelectedOptions
      renderInput={params => <TextField {...params} label={displayName} />}
    />
  );
});

AttributeFilterRow.displayName = "AttributeFilterRow";

const AttributesFilterBase = (props: Props) => {
  const { attributesCatalog, formData, setFormDataAttributes } = props;

  const attributes = useMemo(() => {
    if (attributesCatalog.loading === "has-loaded") {
      return attributesCatalog.data.filter(attribute => attribute.values.length > 0);
    }
    return [];
  }, [attributesCatalog]);

  // Lookup instead of a per-row find() over the whole form state. Unchanged attributes keep the same
  // values array reference, which is what lets AttributeFilterRow's memo hold.
  const selectedValuesByAttributeId = useMemo(() => new Map((formData.attributes ?? []).map(attribute => [attribute.id ?? "", attribute.values])), [formData.attributes]);

  return (
    <>
      {attributes.map(attribute => (
        <AttributeFilterRow key={attribute.id} attribute={attribute} selectedValues={selectedValuesByAttributeId.get(attribute.id)} onChange={setFormDataAttributes} />
      ))}
    </>
  );
};

export const AttributesFilter = connect(mapStateToProps, ownActions)(AttributesFilterBase);
